// Оркестрация выбора интервала доставки «день/время» (RU-курьер).
//
// Чистые правила (окно слотов, cutoff, следующий момент пересчёта) — в
// @/utils/deliveryIntervals; здесь только склейка с Vue: реактивное состояние
// селектов, watch на входы, heartbeat-таймер и document-события со снятием в
// onScopeDispose.
import { computed, onScopeDispose, ref, watch } from 'vue'
import { DateTime } from 'luxon'
import {
  HOLIDAY_TIME,
  buildDeliverySlots,
  buildDeliveryTimePayload,
  buildTimeComment,
  computeNextRecalcAt,
  isExpressService,
  isHolidayOption,
  selectIntervalSource,
} from '@/utils/deliveryIntervals'

const RECALC_HEARTBEAT_MS = 30 * 1000
const CLOCK_JUMP_SLACK_MS = 5 * 1000
const MOSCOW_ZONE = 'Europe/Moscow'

const moscowNow = () => DateTime.now().setZone(MOSCOW_ZONE).setLocale('ru')

const bothTimes = (msk) => {
  const loc = DateTime.now()
  return `local ${loc.toFormat('HH:mm:ss')} (${loc.zoneName}) | мск ${msk.toFormat('HH:mm:ss')}`
}

// Временная диагностика тиков cutoff: выводим всегда,
// снимается из кода после приёмки
const logInfo = (...args) => console.log('[delivery-intervals]', ...args)

/**
 * @param {object} props  mkad / deliveryTimes / deliveryServiceId
 * @param {(event: string, ...payload: unknown[]) => void} emit
 */
export function useDeliveryIntervals(props, emit) {
  const options = ref([])
  const schedule = ref({})
  const day = ref(null)
  const time = ref('')

  const isExpress = computed(() => isExpressService(props.deliveryServiceId))
  const sourceIntervals = computed(() =>
    selectIntervalSource(props.deliveryTimes, {
      isExpress: isExpress.value,
      mkad: props.mkad,
    })
  )
  const isHolidays = computed(() => isHolidayOption(day.value))
  const times = computed(() =>
    isHolidays.value
      ? [HOLIDAY_TIME]
      : (schedule.value[day.value?.id]?.times ?? [])
  )

  function emitSelection() {
    const isSelected = options.value.some((option) => option === day.value)
    if (isSelected) {
      emit('time', buildTimeComment(day.value, time.value))
      emit(
        'deliveryTime',
        buildDeliveryTimePayload(schedule.value, day.value, time.value)
      )
      emit('expressDisabled', false)
    } else if (isExpress.value) {
      emit('expressDisabled', true)
    }
  }

  function recalculate() {
    const now = moscowNow()
    const { options: nextOptions, schedule: nextSchedule } = buildDeliverySlots(
      {
        now,
        intervals: sourceIntervals.value,
        isExpress: isExpress.value,
        restricted: props.deliveryTimes.restricted,
      }
    )

    options.value = nextOptions
    schedule.value = nextSchedule
    day.value = nextOptions[0]
    time.value = times.value[0] ?? ''
    updateRecalcAt(now)
    logInfo(
      `пересчёт: ${bothTimes(now)} | первый день: ${nextOptions[0]?.text ?? '—'}`
    )
  }

  // Heartbeat: московское время сверяется каждые 30 с — ловит переход cutoff,
  // перевод часов в обе стороны, сон и троттлинг вкладки
  let heartbeat = null
  let lastTickAt
  let recalcAt = 0

  function updateRecalcAt(now = moscowNow()) {
    const next = computeNextRecalcAt({
      now,
      intervals: sourceIntervals.value,
      isExpress: isExpress.value,
    })
    recalcAt = next.toMillis()
    logInfo(
      `getNextRecalcAt: сервис ${props.deliveryServiceId} | mkad=${props.mkad} | порог ${next.toFormat('EEE HH:mm')} мск | ${bothTimes(now)}`
    )
  }

  function checkRecalc() {
    const now = moscowNow()
    const nowMs = now.toMillis()
    const delta =
      lastTickAt === undefined ? RECALC_HEARTBEAT_MS : nowMs - lastTickAt
    lastTickAt = nowMs
    const jumpedBack = delta < -CLOCK_JUMP_SLACK_MS
    const shouldRecalc = (!!recalcAt && nowMs >= recalcAt) || jumpedBack

    if (recalcAt) {
      const jumpNote = jumpedBack
        ? ` | скачок часов ${Math.round(-delta / 60000)} мин назад`
        : ''
      logInfo(
        `${bothTimes(now)} | до порога ${DateTime.fromMillis(recalcAt).setZone(MOSCOW_ZONE).toFormat('HH:mm:ss')} | ${shouldRecalc ? 'ПЕРЕСЧЁТ' : 'ожидание'}${jumpNote}`
      )
    }

    if (shouldRecalc) recalculate()
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') checkRecalc()
  }

  watch(() => props.mkad, recalculate)
  watch(() => props.deliveryServiceId, recalculate)
  watch(day, () => {
    time.value = times.value[0] ?? ''
  })
  watch([day, time], emitSelection)

  {
    const btList = (key) =>
      (props.deliveryTimes[key] ?? [])
        .map((day) => day?.beforeTime ?? '—')
        .join(',')
    logInfo(
      `данные интервалов: mkad bt=[${btList('mkad')}], outside bt=[${btList('outside')}], express bt=[${btList('express')}]`
    )
  }
  recalculate()
  logInfo(`heartbeat запущен, интервал ${RECALC_HEARTBEAT_MS / 1000}с`)
  heartbeat = setInterval(checkRecalc, RECALC_HEARTBEAT_MS)
  document.addEventListener('visibilitychange', onVisibilityChange)
  onScopeDispose(() => {
    clearInterval(heartbeat)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  })

  return { options, schedule, day, time, times, isHolidays, recalculate }
}
